import { Component, OnInit, OnDestroy, ChangeDetectorRef, AfterViewChecked, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { NotificationService, Notification, NotificationType } from '../../services/notification.service';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss']
})
export class NotificationComponent implements OnInit, OnDestroy, AfterViewChecked {
  currentNotification: Notification | null = null;
  private subscription?: Subscription;
  /** Tracks which notification ID has already had its rows font-scaled.
   *  Prevents re-running the DOM measurement on every CD cycle. */
  private lastScaledNotifId: string | null = null;

  @ViewChildren('detailEl') detailEls!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Subscribe to notification queue - show one at a time
    this.subscription = this.notificationService.getNotifications().subscribe(notifications => {
      const next = notifications[0] || null;
      setTimeout(() => {
        this.currentNotification = next;
        this.lastScaledNotifId = null;
        try {
          this.cdr.detectChanges();
        } catch (_) {}
      }, 0);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    // Only run once per notification (guard prevents per-cycle thrashing)
    if (!this.currentNotification || this.lastScaledNotifId === this.currentNotification.id) {
      return;
    }
    if (!this.detailEls || this.detailEls.length === 0) {
      return;
    }

    this.lastScaledNotifId = this.currentNotification.id;

    // Keep the shared font size consistent across every toaster; long values truncate via
    // .detail-value ellipsis + the existing [title] tooltip shows the full originalValue on hover.
    this.detailEls.forEach(elRef => {
      const row = elRef.nativeElement;
      const valueEl = row.querySelector('.detail-value') as HTMLElement | null;
      if (valueEl) {
        valueEl.style.fontSize = '';
      }
    });
  }

  /**
   * Pause auto-dismiss while user hovers over the notification
   */
  pauseDismiss(): void {
    this.notificationService.pauseDismiss();
  }

  /**
   * Resume auto-dismiss (fresh 5s) when mouse leaves
   */
  resumeDismiss(): void {
    this.notificationService.resumeDismiss();
  }

  /**
   * Dismiss current notification
   */
  dismiss(): void {
    if (this.currentNotification) {
      this.notificationService.dismiss(this.currentNotification.id);
    }
  }

  /**
   * Get notification type class
   */
  getTypeClass(): string {
    if (!this.currentNotification) return '';
    return `notification-${this.currentNotification.type}`;
  }

  /**
   * Get icon for notification type
   */
  getIcon(): string {
    if (!this.currentNotification) return '';
    
    switch (this.currentNotification.type) {
      case NotificationType.SUCCESS:
        return '✓';
      case NotificationType.ERROR:
        return '✕';
      case NotificationType.WARNING:
        return '⚠';
      case NotificationType.INFO:
        return 'ℹ';
      default:
        return '';
    }
  }
}
